/**
 * Applies indexed bonding-curve state from a market trade event.
 *
 * Context: the typed trade event (`morfi.market.{m}.user.*.trade.v0`,
 * proto `morfi.v1.MarketTrade`) carries `postBcQuoteReservesD6` — the
 * authoritative virtual-inclusive post-trade quote reserve of the TRADED
 * outcome — plus `outcomeIdx`. Trade events also carry
 * `postMarketRealQuoteReservesD6`, the authoritative market-wide real quote
 * reserve total across active outcomes. The manifest recipe is a HELPER line:
 *
 *   set: { outcomes[outcomeIdx].effectiveMarketCapD6: postBcQuoteReservesD6, helper: applyBcActivityToMarket }
 *
 * Codegen emits ONLY `applyBcActivityToMarket(row, raw)` for a helper line
 * (the helper owns the transform end-to-end — see `gen.ts#emitRecipeOps`),
 * so this helper does the whole job in two steps:
 *   1. derive the traded outcome's effective market cap from
 *      `postBcQuoteReservesD6` and its virtual quote reserve.
 *   2. set `totalReserves` only from a valid `postMarketRealQuoteReservesD6`.
 *
 * Immutable: returns a NEW object only when something actually changes;
 * otherwise the exact `prev` reference — keeps the structural-sharing /
 * `row === prev` short-circuit intact and is idempotent on JetStream
 * replay.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { effectiveMcapD6 } from './effective-mcap.js';

function toNonNegativeIntegerString(v: unknown): string | null {
  if (typeof v === 'bigint') return v >= 0n ? v.toString() : null;
  if (typeof v === 'number') {
    if (!Number.isSafeInteger(v) || v < 0) return null;
    return BigInt(v).toString();
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '' || !/^\d+$/.test(trimmed)) return null;
    try {
      return BigInt(trimmed).toString();
    } catch {
      return null;
    }
  }
  return null;
}

function toOutcomeIndex(v: unknown): number | null {
  const value = toNonNegativeIntegerString(v);
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

type BondingCurvePoolState = {
  readonly virtualQuoteReserveD6?: string;
  readonly virtualInclusiveQuoteReserveD6?: string;
  readonly outstandingOutcomeSupplyD6?: string;
};

function isBondingCurvePoolState(
  state: BondingCurvePoolState | undefined,
): state is Required<BondingCurvePoolState> {
  return state?.virtualQuoteReserveD6 !== undefined;
}

/**
 * Read a field from a decoded realtime event — handles both the flat
 * protobuf-decoded shape (`raw[key]`, the live wire) and the
 * `.payload`-nested shape (`raw.payload[key]`, JSON test fixtures),
 * payload-nested first — mirroring codegen's `compilePayloadPath`.
 */
function readEventField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in (nested as object)) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

/**
 * Apply one decoded `MarketTrade` event to a market row's
 * effective market cap, then set `totalReserves` from an authoritative emitted
 * total when available.
 *
 * `prev` is the cached market row (any shape carrying an optional
 * `totalReserves` D6 string + an `outcomes` array whose items carry named
 * outcome identity and bonding-curve state — the codegen-emitted caller passes
 * the concrete `GetMarketRow` / `GetMarketsRow`). `raw` is the decoded event.
 *
 * Returns `prev` verbatim when there is no `outcomes` array, or when
 * neither the patched market cap nor the authoritative total changes anything.
 */
export function applyBcActivityToMarket<
  T extends {
    readonly totalReserves?: string;
    readonly outcomes?: ReadonlyArray<{
      readonly outcomeIndex?: number;
      readonly graduated?: boolean;
      readonly latestPoolQuoteD6?: string;
      readonly claimRightsSupplyD6?: string;
      readonly currentPoolState?: {
        readonly virtualQuoteReserveD6?: string;
        readonly virtualInclusiveQuoteReserveD6?: string;
        readonly outstandingOutcomeSupplyD6?: string;
      };
      readonly effectiveMarketCapD6?: string;
    }>;
  },
>(prev: T, raw: Record<string, unknown>): T {
  const outcomes = prev.outcomes;
  if (!Array.isArray(outcomes)) return prev;

  // (1) Patch the current bonding-curve state and derived market cap.
  let nextOutcomes: ReadonlyArray<{
    readonly outcomeIndex?: number;
    readonly graduated?: boolean;
    readonly latestPoolQuoteD6?: string;
    readonly claimRightsSupplyD6?: string;
    readonly currentPoolState?: {
      readonly virtualQuoteReserveD6?: string;
      readonly virtualInclusiveQuoteReserveD6?: string;
      readonly outstandingOutcomeSupplyD6?: string;
    };
    readonly effectiveMarketCapD6?: string;
  }> = outcomes;
  const idx = toOutcomeIndex(readEventField(raw, 'outcomeIdx'));
  const poolQuote = toNonNegativeIntegerString(readEventField(raw, 'postBcQuoteReservesD6'));
  const outcomePosition =
    idx === null ? -1 : outcomes.findIndex((outcome) => outcome.outcomeIndex === idx);
  if (poolQuote !== null && outcomePosition >= 0) {
    const current = outcomes[outcomePosition];
    const currentState = current?.currentPoolState;
    const virtualQuote =
      current?.graduated !== true && isBondingCurvePoolState(currentState)
        ? toNonNegativeIntegerString(currentState.virtualQuoteReserveD6)
        : null;
    const computedMarketCap =
      virtualQuote !== null && BigInt(virtualQuote) > 0n
        ? effectiveMcapD6(BigInt(poolQuote), BigInt(virtualQuote))
        : null;
    const nextMarketCap = computedMarketCap?.toString();

    const nextQuoteState =
      current?.graduated !== true &&
      isBondingCurvePoolState(currentState) &&
      currentState.virtualInclusiveQuoteReserveD6 !== poolQuote
        ? { ...currentState, virtualInclusiveQuoteReserveD6: poolQuote }
        : currentState;
    const outstandingSupply = toNonNegativeIntegerString(readEventField(raw, 'postBcSupplyD6'));
    const nextState =
      current?.graduated !== true &&
      isBondingCurvePoolState(nextQuoteState) &&
      outstandingSupply !== null &&
      nextQuoteState.outstandingOutcomeSupplyD6 !== outstandingSupply
        ? { ...nextQuoteState, outstandingOutcomeSupplyD6: outstandingSupply }
        : nextQuoteState;
    const nextLatestPoolQuoteD6 =
      current !== undefined &&
      'latestPoolQuoteD6' in current &&
      current.latestPoolQuoteD6 !== poolQuote
        ? poolQuote
        : current?.latestPoolQuoteD6;
    const nextClaimRightsSupplyD6 =
      current !== undefined &&
      outstandingSupply !== null &&
      'claimRightsSupplyD6' in current &&
      current.claimRightsSupplyD6 !== outstandingSupply
        ? outstandingSupply
        : current?.claimRightsSupplyD6;

    if (
      current !== undefined &&
      ((nextMarketCap !== undefined && current.effectiveMarketCapD6 !== nextMarketCap) ||
        nextState !== currentState ||
        nextLatestPoolQuoteD6 !== current.latestPoolQuoteD6 ||
        nextClaimRightsSupplyD6 !== current.claimRightsSupplyD6)
    ) {
      nextOutcomes = outcomes.map((o, i) => {
        if (i !== outcomePosition) return o;
        return {
          ...o,
          ...(nextMarketCap !== undefined ? { effectiveMarketCapD6: nextMarketCap } : {}),
          ...(nextLatestPoolQuoteD6 !== current.latestPoolQuoteD6
            ? { latestPoolQuoteD6: nextLatestPoolQuoteD6 }
            : {}),
          ...(nextClaimRightsSupplyD6 !== current.claimRightsSupplyD6
            ? { claimRightsSupplyD6: nextClaimRightsSupplyD6 }
            : {}),
          ...(nextState !== currentState ? { currentPoolState: nextState } : {}),
        };
      });
    }
  }

  // (2) Market-wide real quote total is required on MarketTrade. Preserve the
  // REST seed only when the payload is absent/malformed (e.g. non-trade
  // activity shapes). Per-outcome BC state cannot reconstruct this value.
  const emittedTotal = toNonNegativeIntegerString(
    readEventField(raw, 'postMarketRealQuoteReservesD6'),
  );
  const total = emittedTotal ?? prev.totalReserves;

  if (nextOutcomes === outcomes && prev.totalReserves === total) return prev;
  return { ...prev, outcomes: nextOutcomes, totalReserves: total };
}
